import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Loader2, Pencil, Radio, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

import { adminApi, api } from "@/lib/api";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

const EMPTY_FORM = { title: "", video_url: "", comment: "" };

export default function AdminBlog() {
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    useEffect(() => {
        if (!localStorage.getItem("ms_admin_token")) {
            navigate("/admin/login");
            return;
        }
        load();
    }, [navigate]);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/blog");
            setPosts(r.data?.posts || []);
        } catch {
            toast.error("Erreur de chargement");
        } finally {
            setLoading(false);
        }
    };

    const startEdit = (p) => {
        setEditingId(p.id);
        setForm({ title: p.title, video_url: p.video_url, comment: p.comment });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.video_url.trim() || !form.comment.trim()) {
            toast.error("Titre, lien vidéo et commentaire sont requis");
            return;
        }
        setSaving(true);
        try {
            if (editingId) {
                await adminApi.patch(`/admin/blog/${editingId}`, form);
                toast.success("Article mis à jour");
            } else {
                await adminApi.post("/admin/blog", form);
                toast.success("Vidéo publiée");
            }
            cancelEdit();
            await load();
        } catch (err) {
            if (err?.response?.status === 401) {
                localStorage.removeItem("ms_admin_token");
                navigate("/admin/login");
            } else {
                toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement");
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (p) => {
        if (!window.confirm(`Supprimer « ${p.title} » ?`)) return;
        try {
            await adminApi.delete(`/admin/blog/${p.id}`);
            toast.success("Article supprimé");
            if (editingId === p.id) cancelEdit();
            await load();
        } catch {
            toast.error("Suppression impossible");
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-blog-page">
            <SEO title="Admin — Blog vidéo · Studio Mon Séraphin" description="Gestion du blog vidéo" path="/admin/blog" noindex />
            <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl">
                <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Radio className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
                        <div>
                            <p className="font-display text-sm font-medium">Mon Séraphin · Admin</p>
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Blog vidéo</p>
                        </div>
                    </div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="admin-blog-back-btn">
                        <ArrowLeft className="w-4 h-4" /> Réservations
                    </Link>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-10">
                <form onSubmit={handleSubmit} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6" data-testid="admin-blog-form">
                    <h1 className="font-display text-lg font-medium">
                        {editingId ? "Modifier la vidéo" : "Publier une vidéo"}
                    </h1>
                    <p className="mt-1 text-xs text-zinc-500 font-body">
                        Lien accepté : YouTube Shorts, TikTok, Instagram Reel ou fichier MP4 direct.
                        Le commentaire est lu par Google et les IA (ChatGPT, Perplexity…) — soyez précis : contexte, conseils, coulisses.
                    </p>
                    <div className="mt-5 space-y-4">
                        <input
                            type="text"
                            value={form.title}
                            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                            placeholder="Titre de la vidéo"
                            className="w-full rounded-md border border-white/10 bg-white/[0.02] px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-amber-300/50 focus:outline-none"
                            data-testid="admin-blog-title-input"
                        />
                        <input
                            type="url"
                            value={form.video_url}
                            onChange={(e) => setForm((f) => ({ ...f, video_url: e.target.value }))}
                            placeholder="https://youtube.com/shorts/… ou https://tiktok.com/… ou https://…/video.mp4"
                            className="w-full rounded-md border border-white/10 bg-white/[0.02] px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-amber-300/50 focus:outline-none"
                            data-testid="admin-blog-video-input"
                        />
                        <textarea
                            value={form.comment}
                            onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
                            placeholder="Votre commentaire éditorial : contexte, conseils, coulisses de la vidéo…"
                            rows={6}
                            className="w-full rounded-md border border-white/10 bg-white/[0.02] px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-amber-300/50 focus:outline-none"
                            data-testid="admin-blog-comment-input"
                        />
                    </div>
                    <div className="mt-5 flex items-center gap-3">
                        <Button
                            type="submit"
                            disabled={saving}
                            className="bg-amber-400 text-black hover:bg-amber-300 rounded-full px-6"
                            data-testid="admin-blog-submit-btn"
                        >
                            {saving ? (
                                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Enregistrement…</>
                            ) : editingId ? (
                                "Mettre à jour"
                            ) : (
                                "Publier"
                            )}
                        </Button>
                        {editingId && (
                            <Button type="button" variant="ghost" onClick={cancelEdit} className="text-zinc-400 hover:text-white" data-testid="admin-blog-cancel-edit-btn">
                                Annuler
                            </Button>
                        )}
                    </div>
                </form>

                <div className="mt-10">
                    <h2 className="font-display text-lg font-medium">Vidéos publiées ({posts.length})</h2>
                    {loading ? (
                        <p className="mt-6 text-sm text-zinc-500">Chargement…</p>
                    ) : posts.length === 0 ? (
                        <p className="mt-6 text-sm text-zinc-500" data-testid="admin-blog-empty">Aucune vidéo publiée pour le moment.</p>
                    ) : (
                        <ul className="mt-5 space-y-3" data-testid="admin-blog-list">
                            {posts.map((p) => (
                                <li
                                    key={p.id}
                                    className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4"
                                    data-testid={`admin-blog-item-${p.id}`}
                                >
                                    <div className="min-w-0">
                                        <p className="font-display text-sm font-medium text-white truncate">{p.title}</p>
                                        <p className="mt-0.5 text-xs text-zinc-500 font-body truncate">
                                            {format(new Date(p.created_at), "d MMM yyyy", { locale: fr })} · /blog/{p.slug}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <Link
                                            to={`/blog/${p.slug}`}
                                            className="p-2 text-zinc-400 hover:text-amber-300 transition-colors"
                                            title="Voir la page publique"
                                            data-testid={`admin-blog-view-${p.slug}`}
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                        </Link>
                                        <button
                                            onClick={() => startEdit(p)}
                                            className="p-2 text-zinc-400 hover:text-white transition-colors"
                                            title="Modifier"
                                            data-testid={`admin-blog-edit-${p.id}`}
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(p)}
                                            className="p-2 text-zinc-400 hover:text-red-400 transition-colors"
                                            title="Supprimer"
                                            data-testid={`admin-blog-delete-${p.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </main>
        </div>
    );
}
